/**
 * Narrowing the mark tree to what a reader is looking for.
 *
 * The type-ahead in `markNavigation` is the standard listbox one: it jumps the active row to the
 * next label starting with what was typed, within half a second. That answers "take me to the
 * Fs". It does not answer "where is Payments Gateway", because it matches on prefix, it moves one
 * row rather than narrowing, and it is invisible unless you already know it is there.
 *
 * This answers the question as asked: every label containing the text, counted, kept in its place
 * in the grouping, and handed back as ids so the canvas can light them all at once.
 */

import type { MarkTreeItem } from './markNavigation';

export type MarkFilter<T extends MarkTreeItem> = {
  /** The rows to show, re-numbered so a screen reader counts what is on screen. */
  items: readonly T[];
  /** Rows that matched on their own, not the ancestors kept to place them. */
  matchedIds: readonly string[];
};

/**
 * Case-insensitive, on the label and nothing else.
 *
 * Not on `details`, which carry the type name and the member count - searching there would make
 * "Application" return every component of that type, which is a different question and a
 * surprising answer to this one.
 */
const isMatch = (item: MarkTreeItem, needle: string): boolean =>
  item.label.toLocaleLowerCase().includes(needle);

const toAncestorIds = <T extends MarkTreeItem>(
  item: T,
  byId: ReadonlyMap<string, T>
): string[] => {
  const ancestors: string[] = [];
  let parentId = item.parentId;
  /* By id rather than by walking back over `level`, because the list is the tree already
     flattened and a sibling subtree sits between a row and its parent. */
  while (parentId !== null) {
    const parent = byId.get(parentId);
    if (parent === undefined) break;
    ancestors.push(parent.id);
    parentId = parent.parentId;
  }
  return ancestors;
};

/**
 * Renumbered against what survived, and disclosed against it too.
 *
 * `setSize` and `posInSet` are what a screen reader reads out as "3 of 47", so carrying the
 * unfiltered numbers through would announce a list the reader cannot see. Expansion is rewritten
 * for the same reason: a row whose children were all filtered out has nothing left to disclose,
 * and a triangle offering to open it would be a control that does nothing.
 */
const toRenumbered = <T extends MarkTreeItem>(
  kept: readonly T[],
  collapsedIds: ReadonlySet<string>,
  /** Rows that had a kept child before the fold hid it, so opening them shows something. */
  foldingSomething: ReadonlySet<string>
): T[] => {
  const siblingCounts = new Map<string, number>();
  for (const { parentId } of kept) {
    const key = parentId ?? '';
    siblingCounts.set(key, (siblingCounts.get(key) ?? 0) + 1);
  }

  const hasKeptChildren = new Set(
    kept.map(({ parentId }) => parentId).filter(id => id !== null)
  );

  const seen = new Map<string, number>();
  return kept.map(item => {
    const key = item.parentId ?? '';
    const posInSet = (seen.get(key) ?? 0) + 1;
    seen.set(key, posInSet);
    return {
      ...item,
      setSize: siblingCounts.get(key) ?? 1,
      posInSet,
      /* Closed only while there is something behind it. A host-folded group always qualifies -
         its members are not in the graph, so opening it asks the host for them - but a row the
         reader closed qualifies only if the fold is what is hiding its results. `collapsedIds`
         outlives a search, so a row closed earlier can be matched by its own name with nothing
         inside it matching, and marking that closed offers to open onto nothing. */
      expansion: hasKeptChildren.has(item.id)
        ? ('expanded' as const)
        : item.expansion === 'collapsed' ||
            (collapsedIds.has(item.id) && foldingSomething.has(item.id))
          ? ('collapsed' as const)
          : ('leaf' as const),
    };
  });
};

/**
 * Rows hidden because an ancestor of theirs is closed.
 *
 * Applied after matching rather than before it, which is the whole point: a search covers the
 * graph, so closing a row narrows what the *list* shows without taking anything out of the result
 * set or off the canvas.
 */
const toHiddenIds = <T extends MarkTreeItem>(
  kept: readonly T[],
  collapsedIds: ReadonlySet<string>
): Set<string> => {
  const hidden = new Set<string>();
  /* Top-down, so a parent's own state is settled before its children ask about it. */
  for (const { id, parentId } of kept) {
    if (parentId === null) continue;
    if (hidden.has(parentId) || collapsedIds.has(parentId)) hidden.add(id);
  }
  return hidden;
};

/**
 * The tree as the query leaves it.
 *
 * A blank query is not a filter: everything is returned untouched, and nothing is reported as
 * matching, so a caller can tell "showing all of it" from "found none of it".
 */
export const toMarkFilter = <T extends MarkTreeItem>(
  items: readonly T[],
  query: string,
  /** Rows the reader has closed. Narrows what is listed, never what was found. */
  collapsedIds: ReadonlySet<string> = new Set()
): MarkFilter<T> => {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') return { items, matchedIds: [] };

  const byId = new Map(items.map(item => [item.id, item]));
  const matchedIds: string[] = [];
  const keptIds = new Set<string>();

  for (const item of items) {
    if (!isMatch(item, needle)) continue;
    matchedIds.push(item.id);
    keptIds.add(item.id);
    /* Kept so a result reads as somewhere rather than as a loose name. A reader looking for one
       component is usually also asking which part of the estate it is in. */
    for (const ancestorId of toAncestorIds(item, byId)) keptIds.add(ancestorId);
  }

  const kept = items.filter(({ id }) => keptIds.has(id));
  const hidden = toHiddenIds(kept, collapsedIds);
  /* Taken before the hiding, which is the whole point: afterwards a closed row has no children
     left to count, so it cannot answer whether it is hiding any. */
  const foldingSomething = new Set(
    kept
      .map(({ parentId }) => parentId)
      .filter((id): id is string => id !== null)
  );

  return {
    items: toRenumbered(
      kept.filter(({ id }) => !hidden.has(id)),
      collapsedIds,
      foldingSomething
    ),
    matchedIds,
  };
};
